/**
 * Picks readable text color for arbitrary hex backgrounds (e.g. kit primaries).
 * Uses relative luminance — avoids white-on-yellow and dark-on-charcoal mistakes.
 * Does not use semantic theme grays (those invert in dark mode).
 */

function normalizeHex(hex: string): string | null {
  const h = hex.trim();
  if (!h.startsWith('#')) return null;
  const body = h.slice(1);
  if (body.length === 3 && /^[0-9A-Fa-f]{3}$/.test(body)) {
    return `#${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`.toUpperCase();
  }
  if (body.length === 6 && /^[0-9A-Fa-f]{6}$/.test(body)) {
    return `#${body.toUpperCase()}`;
  }
  return null;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const n = normalizeHex(hex);
  if (n === null) return null;
  return {
    r: parseInt(n.slice(1, 3), 16),
    g: parseInt(n.slice(3, 5), 16),
    b: parseInt(n.slice(5, 7), 16),
  };
}

/** sRGB relative luminance, W3C formula, range ~0–1 */
function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (rgb === null) return 0.4;

  const linearize = (c: number): number => {
    const x = c / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };

  const r = linearize(rgb.r);
  const g = linearize(rgb.g);
  const b = linearize(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Dark glyphs on light fills; light glyphs on dark fills */
const TEXT_ON_LIGHT_BG = '#111827';
const TEXT_ON_DARK_BG = '#FFFFFF';

/**
 * Returns a fixed near-black or white hex for readable text on `backgroundHex`.
 * Biased toward white text — only switches to dark on clearly light fills
 * (white, pale yellows/pinks), so saturated kit primaries keep white glyphs.
 */
export function pickContrastTextOnBackground(backgroundHex: string): string {
  const lum = relativeLuminance(backgroundHex);
  return lum > 0.6 ? TEXT_ON_LIGHT_BG : TEXT_ON_DARK_BG;
}
